import { useCallback } from 'react'
import {
  FieldDesignChange as FDC,
  FdcConcurrenceLevel,
  ProjectMetadata,
} from 'schema'
import { useCheckPermission } from 'hooks/useCheckPermission'
import {
  FDCConcurrenceLevelSideEffect,
  FieldDesignChangeStatus,
  Permission,
} from '@cims-online/shared'
import { notificationHandler } from 'hooks/notificationHandler'
import EarlyClosureNotificationDetails from './components/EarlyClosureNotificationDetails'
import { useCIMSModal } from 'components/CIMSModal/hooks/useCIMSModal'
import { toLabeledValues } from 'components/CIMSSelect/utils/labeledValues'
import {
  CreateDesignSubmittalModal,
  CreateDesignSubmittalModalData,
} from '../../../../../DesignSubmittals/components/DesignSubmittalLog/components/DesignSubmittalLogTable/hooks/useCreateOptions/components/CreateDesignSubmittalModal'

type FieldDesignChange = Pick<FDC, 'id' | 'formattedNumber' | 'statusId'> & {
  project: {
    projectMetadata: Pick<
      ProjectMetadata,
      'promptDesignSubmittalAtFDCClosure'
    > | null
  }
  concurrenceLevel: Pick<
    FdcConcurrenceLevel,
    'sideEffectId' | 'name' | 'description'
  > | null
}

/**
 * builds a callback to trigger a create design submittal modal, along with the
 * modal itself
 *
 * @param onClose a callback to execute after the create design submittal modal is closed
 */
export function useTriggerEarlyClosureNotification(
  onClose: (...args: any[]) => any
) {
  const [visible, toggle, createData] =
    useCIMSModal<Pick<CreateDesignSubmittalModalData, 'fdcIds'>>()
  const canCreateDesignSubmittal = useCheckPermission(
    Permission.CreateDesignSubmittal
  )
  const callback = useCallback<
    (
      fieldDesignChange: FieldDesignChange,
      action: 'submitted' | 'edited'
    ) => void
  >(
    (fieldDesignChange, action) => {
      const { statusId, concurrenceLevel, project, id, formattedNumber } =
        fieldDesignChange
      const prompt =
        statusId === FieldDesignChangeStatus.Closed.id &&
        concurrenceLevel?.sideEffectId ===
          FDCConcurrenceLevelSideEffect.EarlyClosure.id &&
        project.projectMetadata?.promptDesignSubmittalAtFDCClosure &&
        canCreateDesignSubmittal
      return notificationHandler({
        key: id,
        title: `${formattedNumber} successfully ${action}!`,
        alternateType: 'success',
        duration: prompt ? 'infinite' : undefined,
        details: prompt && (
          <EarlyClosureNotificationDetails
            fieldDesignChange={fieldDesignChange}
            onCreate={() =>
              toggle({
                fdcIds: toLabeledValues([fieldDesignChange], {
                  value: 'id',
                  label: 'formattedNumber',
                }),
              })
            }
          />
        ),
      })
    },
    [canCreateDesignSubmittal, toggle]
  )

  return [
    callback,
    <CreateDesignSubmittalModal
      visible={visible}
      state={createData}
      onClose={() => {
        toggle(null)
        onClose()
      }}
    />,
  ] as const
}
